import NextImage from 'next/image'
import { getLink } from '@helpers/env'
import SectionHeader from '@components/ui/SectionHeader'
import styles from './styles.module.scss'
import { analytics } from '@helpers/analytics'
import { jsonToArray } from '@helpers/contentful'
import Badge, { BadgeProps } from '@components/ui/Badge'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { type Button as ButtonProps } from 'types/contentful'
import {
  getPageCategoryProductBreakdown,
  type SendEventProperties
} from '@components/ui/ProductsBreakdown/PBGrid'
import { upperFirstLetter } from '@helpers/utils'
import classNames from 'classnames'
import { ButtonLink } from '@components/ui/Button'

const sendEvent = ({
  componentType,
  category,
  title,
  firstTimeUser
}: SendEventProperties) => {
  const props = {
    action: `Breakdown Collection ${componentType}`,
    category: category || getPageCategoryProductBreakdown(),
    label: !!title
      ? upperFirstLetter(title.toLowerCase())
      : 'Collection Breakdown',
    type: firstTimeUser ? 'New' : 'Existing'
  }
  analytics('Click', props)
}

type CardProps = {
  title: string
  category: string
  image: string
  handle: string
  badge?: BadgeProps
  theme: 'light' | 'dark'
  button: ButtonProps
  showTitle: boolean
  showButton: boolean
}

function Card({
  card,
  firstTimeUser
}: {
  card: CardProps
  firstTimeUser: boolean
}) {
  const {
    category,
    handle,
    title,
    theme,
    image,
    badge,
    button,
    showButton = true,
    showTitle = true
  } = card || {}

  const handleLinkClick = () => {
    sendEvent({
      componentType: 'Image',
      title,
      category,
      firstTimeUser
    })
  }

  if (!showButton || !handle) {
    return (
      <div
        className={`${styles.ProductsBreakdown__Card} ${
          styles[`theme_${theme}`]
        }`}
      >
        {badge && (
          <Badge
            classes={styles.ProductsBreakdown__Card__badge}
            title={badge.title}
            themeColour={badge.themeColour}
          />
        )}
        {showTitle && (
          <h3 className={styles.ProductsBreakdown__CardTitle}>{title}</h3>
        )}

        <NextImage alt={title} src={image} width="320" height="438" />
      </div>
    )
  }

  return (
    <a
      className={`${styles.ProductsBreakdown__Card} ${
        styles[`theme_${theme}`]
      }`}
      href={getLink(handle)}
      onClick={handleLinkClick}
      aria-label={`Shop ${title}`}
    >
      {badge && (
        <Badge
          classes={styles.ProductsBreakdown__Card__badge}
          title={badge.title}
          themeColour={badge.themeColour}
        />
      )}
      {showTitle && (
        <h3 className={styles.ProductsBreakdown__CardTitle}>{title}</h3>
      )}

      <NextImage alt={title} src={image} width="320" height="438" />
      {button && (
        <ButtonMinimal
          classes={styles.ProductsBreakdown__CardButton}
          variant={theme === 'dark' ? 'primary' : 'white'}
          underlined={true}
        >
          {button.text.trim()}
        </ButtonMinimal>
      )}
    </a>
  )
}

type CardItem = {
  title: string
  badge?: {
    title: string
  }
  category: string
  button: ButtonProps
  handle: string
  theme: 'light' | 'dark'
  image: {
    url?: string
    file: {
      url: string
    }
  }
  showTitle: boolean
  showButton: boolean
}

function productBreakdownCards(products: CardItem[]): CardProps[] {
  return jsonToArray(products || {}).map(
    ({
      image,
      title,
      handle,
      theme,
      badge,
      category,
      button,
      showTitle,
      showButton
    }) => {
      return {
        title: title,
        image: `https:${image?.file.url}`,
        handle: handle,
        theme: theme,
        badge,
        category,
        button,
        showTitle,
        showButton
      }
    }
  )
}

export type LandingPageProductsCardsProps = {
  categories: CardItem[]
  title: string
  firstTimeUser: boolean
  variant: 'breakdown' | 'grid'
  button?: ButtonProps
}

export default function LandingPageProductsCards({
  categories: products,
  title,
  variant = 'breakdown',
  firstTimeUser,
  button
}: LandingPageProductsCardsProps) {
  const cards = productBreakdownCards(products)
  const isGrid = variant === 'grid'
  const sectionClassnames = classNames(
    styles.ProductsBreakdown,

    {
      [styles.ProductsBreakdownGrid]: isGrid
    }
  )

  return (
    <>
      {title && (
        <div className={styles.ProductsBreakdown__header}>
          <SectionHeader title={title} variant="left_aligned" />
        </div>
      )}
      <div className={sectionClassnames}>
        {!!cards?.length && (
          <ul className={styles.ProductsBreakdown__products}>
            {cards.map((card, index) => {
              return (
                <li key={`${card.title}-${index}`}>
                  <Card card={card} firstTimeUser={firstTimeUser} />
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {button && (
        <div className={styles.ProductsBreakdownButton}>
          <ButtonLink href={button.uri} variant={button.buttonStyle}>
            {button.text}
          </ButtonLink>
        </div>
      )}
    </>
  )
}
